import { useMemo } from "react";
import { Briefcase, FolderOpen, HardHat, Layers3 } from "lucide-react";
import type { Document } from "@/types/documents";
import type { BuildingProject } from "@/types/projects";
import type { AvailableFilters } from "@/stores/documents/repository";
import type { Tag, TagGroup, TagGroupColor } from "../components/filters/types";
import { useLanguage } from "@/lib/i18n/LanguageContext";

interface UseTagGroupsProps {
  /** Documents to extract tags from (used when availableFilters not provided) */
  documents: Document[];
  /** Projects for project tag extraction */
  projects?: BuildingProject[];
  /**
   * Pre-fetched available filters from the store.
   * When provided, uses these values instead of extracting from documents.
   * This ensures ALL filter values are shown, not just those in currently loaded documents.
   */
  availableFilters?: AvailableFilters | null;
}

interface TagGroupDefinition {
  id: string;
  labelKey: "projects" | "categories" | "designDisciplines" | "designStages";
  icon: typeof Briefcase;
  color: TagGroupColor;
}

/**
 * Group definitions for the 4 tag categories.
 * Colors match the brand design:
 * - Projects: project (transparent with dark text)
 * - Categories: section1 (black)
 * - Design Disciplines: section3 (gray)
 * - Design Stages: section2 (tan/brown)
 */
const TAG_GROUP_DEFINITIONS: TagGroupDefinition[] = [
  {
    id: "projects",
    labelKey: "projects",
    icon: Briefcase,
    color: "project",
  },
  {
    id: "categories",
    labelKey: "categories",
    icon: FolderOpen,
    color: "section1",
  },
  {
    id: "disciplines",
    labelKey: "designDisciplines",
    icon: HardHat,
    color: "section3",
  },
  {
    id: "stages",
    labelKey: "designStages",
    icon: Layers3,
    color: "section2",
  },
];

// When server-provided filter values exist, document rows are not an input at
// all. Keep that unused dependency referentially stable so a caller passing a
// fresh fallback array cannot rebuild every group and reset filter-hook state.
const NO_FALLBACK_DOCUMENTS: Document[] = [];

/**
 * Extract project tags from documents.
 * Filter ID format: `project-{project_id}`
 */
function extractProjectTags(documents: Document[], projects: BuildingProject[]): Tag[] {
  const projectCounts = new Map<string, { id: string; name: string; count: number }>();

  // Count documents per project
  documents.forEach((doc) => {
    if (doc.project_id) {
      const projectIdStr = String(doc.project_id);
      const existing = projectCounts.get(projectIdStr);
      if (existing) {
        existing.count += 1;
      } else {
        // Find project name from projects array
        const project = projects.find((p) => String(p.id) === projectIdStr);
        if (project) {
          projectCounts.set(projectIdStr, {
            id: projectIdStr,
            name: project.name,
            count: 1,
          });
        }
      }
    }
  });

  // Convert to Tag array, sorted alphabetically
  return Array.from(projectCounts.values())
    .filter((p) => p.count > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((p) => ({
      id: `project-${p.id}`,
      label: p.name,
      count: p.count,
    }));
}

/**
 * Extract category tags from documents.
 * Uses the M2M categories relation.
 * Filter ID format: `category-{category_name}`
 */
function extractCategoryTags(documents: Document[], language: string): Tag[] {
  const categoryCounts = new Map<string, { name: string; displayName: string; count: number }>();

  documents.forEach((doc) => {
    if (doc.categories && doc.categories.length > 0) {
      doc.categories.forEach((cat: any) => {
        const key = cat.name.toLowerCase();
        const existing = categoryCounts.get(key);
        if (existing) {
          existing.count += 1;
        } else {
          const displayName = language === "et" ? cat.name_et?.trim() || cat.name : cat.name;
          categoryCounts.set(key, {
            name: cat.name,
            displayName,
            count: 1,
          });
        }
      });
    }
  });

  // Convert to Tag array, sorted alphabetically
  return Array.from(categoryCounts.values())
    .filter((c) => c.count > 0)
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((c) => ({
      id: `category-${c.name.toLowerCase()}`,
      label: c.displayName,
      count: c.count,
    }));
}

/**
 * Extract design discipline tags from documents.
 * Uses the M2M design_disciplines relation.
 * Filter ID format: `construction-{discipline_id}` (matches existing format)
 */
function extractDisciplineTags(documents: Document[], language: string): Tag[] {
  const disciplineCounts = new Map<number, { id: number; name: string; count: number }>();

  documents.forEach((doc) => {
    if (doc.design_disciplines && doc.design_disciplines.length > 0) {
      doc.design_disciplines.forEach((discipline) => {
        const existing = disciplineCounts.get(discipline.id);
        if (existing) {
          existing.count += 1;
        } else {
          const displayName =
            language === "en"
              ? discipline.name_en?.trim() || discipline.description?.trim() || discipline.name
              : discipline.name;
          disciplineCounts.set(discipline.id, {
            id: discipline.id,
            name: displayName,
            count: 1,
          });
        }
      });
    }
  });

  // Convert to Tag array, sorted alphabetically
  return Array.from(disciplineCounts.values())
    .filter((d) => d.count > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((d) => ({
      id: `construction-${d.id}`,
      label: d.name,
      count: d.count,
    }));
}

/**
 * Extract design stage tags from documents.
 * Uses the M2M design_stages relation.
 * Filter ID format: `stage-{stage_id}` (matches existing format)
 */
function extractStageTags(documents: Document[], language: string): Tag[] {
  const stageCounts = new Map<number, { id: number; name: string; count: number }>();

  documents.forEach((doc) => {
    if (doc.design_stages && doc.design_stages.length > 0) {
      doc.design_stages.forEach((stage) => {
        const existing = stageCounts.get(stage.id);
        if (existing) {
          existing.count += 1;
        } else {
          const displayName = language === "en" ? stage.name_en?.trim() || stage.name : stage.name;
          stageCounts.set(stage.id, {
            id: stage.id,
            name: displayName,
            count: 1,
          });
        }
      });
    }
  });

  // Convert to Tag array, sorted alphabetically
  return Array.from(stageCounts.values())
    .filter((s) => s.count > 0)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((s) => ({
      id: `stage-${s.id}`,
      label: s.name,
      count: s.count,
    }));
}

/**
 * Build project tags from pre-fetched available filters.
 * Uses the projects array with counts when available, falling back to projectIds.
 */
function buildProjectTagsFromFilters(
  availableFilters: AvailableFilters,
  projects: BuildingProject[]
): Tag[] {
  const projectMap = new Map(projects.map((p) => [Number(p.id), p]));

  // Use the new projects array with counts if available
  if (availableFilters.projects && availableFilters.projects.length > 0) {
    return availableFilters.projects
      .map((filterProject) => {
        const project = projectMap.get(filterProject.id);
        if (!project) return null;
        return {
          id: `project-${filterProject.id}`,
          label: project.name,
          count: filterProject.count,
        };
      })
      .filter((tag): tag is Tag => tag !== null)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  // Fallback to projectIds (no counts)
  return availableFilters.projectIds
    .map((projectId) => {
      const project = projectMap.get(projectId);
      if (!project) return null;
      return {
        id: `project-${projectId}`,
        label: project.name,
        count: 0,
      };
    })
    .filter((tag): tag is Tag => tag !== null)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Build category tags from pre-fetched available filters.
 * Falls back to old category hierarchy when filter groups are empty.
 *
 * Creates hierarchical display:
 * - Primary categories appear first (alphabetically)
 * - Subcategories appear indented under their parent ONLY IF:
 *   1. The parent IS a primary category (isPrimary = true)
 *   2. The subcategory is NOT itself a primary category (isPrimary = false)
 * - Categories that are BOTH primary AND subcategories stay at top level
 */
function buildCategoryTagsFromFilters(
  categories: AvailableFilters["categories"],
  language: string
): Tag[] {
  // Build lookup maps
  const categoryById = new Map(categories.map((c) => [c.id, c]));
  const tagIdByCatId = new Map<number, string>();

  // First pass: create tag ID mapping
  for (const cat of categories) {
    const tagId = `category-${cat.name.toLowerCase()}`;
    tagIdByCatId.set(cat.id, tagId);
  }

  // Build all tags with hierarchy references
  const allTags: Tag[] = [];
  for (const cat of categories) {
    const tagId = tagIdByCatId.get(cat.id);
    if (!tagId) {
      // Defensive guard: prevents runtime crash if data is malformed
      console.error(`Missing tag ID mapping for category ${cat.id} (${cat.name})`);
      continue;
    }

    // Determine if this should display as an indented subcategory
    // Rule: Show indented only if:
    //   1. Has a parent (parentCategoryId != null)
    //   2. Is NOT itself a primary category (isPrimary = false)
    //   3. Parent IS a primary category
    let shouldIndent = false;
    let parentTagId: string | undefined;

    if (!cat.isPrimary && cat.parentCategoryId !== null) {
      const parentCat = categoryById.get(cat.parentCategoryId);
      if (parentCat?.isPrimary) {
        shouldIndent = true;
        parentTagId = tagIdByCatId.get(cat.parentCategoryId);
      }
    }

    // Build child tag IDs for this category (for filter expansion)
    const childTagIds = cat.childCategoryIds
      .map((childId) => tagIdByCatId.get(childId))
      .filter((id): id is string => id !== undefined);

    const displayLabel = language === "et" ? cat.name_et?.trim() || cat.name : cat.name;

    allTags.push({
      id: tagId,
      label: displayLabel,
      count: cat.inclusiveCount, // Use inclusive count for display
      isSubcategory: shouldIndent,
      parentTagId: shouldIndent ? parentTagId : undefined,
      childTagIds: childTagIds.length > 0 ? childTagIds : undefined,
      categoryId: cat.id,
    });
  }

  // Sort hierarchically: primary categories first (alphabetically),
  // then their subcategories underneath (alphabetically)
  return sortTagsHierarchically(allTags);
}

/**
 * Build filter group tags from pre-fetched available filters.
 * Uses the decoupled filter hierarchy instead of classification pipeline hierarchy.
 *
 * Deduplicates items with the same category name within each group, merging their
 * category IDs so filtering captures documents tagged with any of the merged categories.
 *
 * Tag ID formats:
 * - Parent groups: `filter-group-{group_id}`
 * - Child items (merged): `filter-merged-{group_id}-{normalized_name}`
 *
 * NOTE: Parent groups are only added if they have at least one child with documents.
 * This ensures empty parent groups are hidden from the UI.
 */
function buildFilterGroupTags(
  filterGroups: AvailableFilters["filterGroups"],
  language: string
): Tag[] {
  const tags: Tag[] = [];

  const getGroupDisplayLabel = (group: AvailableFilters["filterGroups"][number]) =>
    language === "et" && group.labelEt?.trim() ? group.labelEt.trim() : group.label;

  // Sort groups alphabetically by display label
  const sortedGroups = [...filterGroups].sort((a, b) =>
    getGroupDisplayLabel(a).localeCompare(getGroupDisplayLabel(b))
  );

  for (const group of sortedGroups) {
    const parentTagId = `filter-group-${group.id}`;
    const displayGroupLabel = getGroupDisplayLabel(group);

    // Group items by normalized category name within THIS group to deduplicate
    const itemsByName = new Map<string, typeof group.items>();
    for (const item of group.items) {
      const normalizedName = item.categoryName.trim().toLowerCase();
      if (!itemsByName.has(normalizedName)) {
        itemsByName.set(normalizedName, []);
      }
      itemsByName.get(normalizedName)!.push(item);
    }

    // Sort by normalized name for consistent ordering
    const sortedNames = [...itemsByName.keys()].sort();

    const childTagIds: string[] = [];
    const childTags: Tag[] = [];

    // Build child tags first (deduplicated by name)
    for (const normalizedName of sortedNames) {
      const items = itemsByName.get(normalizedName)!;

      // Collect all category IDs and sum counts
      const categoryIds = items.map((item) => item.documentCategoryId);
      const totalCount = items.reduce((sum, item) => sum + item.count, 0);

      // Skip items with no documents
      if (totalCount === 0) continue;

      // Merged tag ID includes group ID to keep unique across groups
      const tagId = `filter-merged-${group.id}-${normalizedName.replace(/\s+/g, "-")}`;
      childTagIds.push(tagId);

      childTags.push({
        id: tagId,
        label:
          language === "et" && items[0].categoryNameEt?.trim()
            ? items[0].categoryNameEt.trim()
            : items[0].categoryName, // Use original casing from first item
        count: totalCount,
        isSubcategory: true,
        parentTagId,
        categoryIds, // All merged category IDs for filtering
        categoryId: categoryIds[0], // Keep for backward compatibility
      });
    }

    // A single child that just repeats the group's own name is noise: the row
    // reads as the same filter listed twice ("Aruanne (6)" indented under
    // "Aruanne (6)"), and selecting either does the same thing. Collapse to one
    // flat row carrying the child's category ids. Compared on the LOCALISED
    // labels actually rendered, so the Estonian UI collapses what the English
    // one does. A group that also has other children keeps both rows — there
    // the parent still means "all of these".
    const onlyChildRepeatsGroupName =
      childTags.length === 1 &&
      childTags[0].label.trim().toLowerCase() === displayGroupLabel.trim().toLowerCase();

    // Only add parent and children if there are children with documents
    if (childTagIds.length > 0) {
      // Standalone group: linked category only, no real items → flat tag (no
      // indentation). Same treatment for a lone child echoing the group name.
      if (
        onlyChildRepeatsGroupName ||
        (childTags.length === 1 && group.items.length === 1 && group.items[0].id < 0)
      ) {
        tags.push({
          id: parentTagId,
          label: displayGroupLabel,
          count: group.count,
          isSubcategory: false,
          categoryIds: childTags[0].categoryIds,
          categoryId: childTags[0].categoryId,
          // no childTagIds → renders flat, no expand/collapse
        });
      } else {
        // Add parent tag
        tags.push({
          id: parentTagId,
          label: displayGroupLabel,
          count: group.count,
          isSubcategory: false,
          childTagIds,
        });

        // Add all child tags
        tags.push(...childTags);
      }
    }
  }

  return tags;
}

/**
 * Sort tags for hierarchical display:
 * Primary categories alphabetically, with subcategories immediately after their parent.
 */
function sortTagsHierarchically(tags: Tag[]): Tag[] {
  const tagById = new Map(tags.map((t) => [t.id, t]));
  const result: Tag[] = [];

  // Get primary categories (not displayed as subcategories) sorted alphabetically
  const primaryTags = tags
    .filter((t) => !t.isSubcategory)
    .sort((a, b) => a.label.localeCompare(b.label));

  for (const primaryTag of primaryTags) {
    result.push(primaryTag);

    // Add subcategories of this primary (those with parentTagId pointing to this tag)
    if (primaryTag.childTagIds) {
      const subcategoryTags = primaryTag.childTagIds
        .map((id) => tagById.get(id))
        .filter((t): t is Tag => t !== undefined && t.isSubcategory === true)
        .sort((a, b) => a.label.localeCompare(b.label));

      result.push(...subcategoryTags);
    }
  }

  return result;
}

/**
 * Build discipline tags from pre-fetched available filters.
 */
function buildDisciplineTagsFromFilters(
  disciplines: AvailableFilters["designDisciplines"],
  language: string
): Tag[] {
  return disciplines.map((disc) => ({
    id: `construction-${disc.id}`,
    label:
      language === "en" ? disc.name_en?.trim() || disc.description?.trim() || disc.name : disc.name,
    count: disc.count,
  }));
}

/**
 * Build stage tags from pre-fetched available filters.
 */
function buildStageTagsFromFilters(
  stages: AvailableFilters["designStages"],
  language: string
): Tag[] {
  return stages.map((stage) => ({
    id: `stage-${stage.id}`,
    label: language === "en" ? stage.name_en?.trim() || stage.name : stage.name,
    count: stage.count,
  }));
}

/**
 * Hook to compute tag groups with counts from documents array.
 *
 * Returns 4 tag groups:
 * - Projects (blue): from document.project_id matching against projects array
 * - Categories (purple): from document.categories (M2M relation)
 * - Design Disciplines (emerald): from document.design_disciplines (M2M relation)
 * - Design Stages (amber): from document.design_stages (M2M relation)
 *
 * Filter ID format (for backward compatibility):
 * - Projects: `project-{project_id}`
 * - Categories: `category-{category_name}`
 * - Disciplines: `construction-{discipline_id}`
 * - Stages: `stage-{stage_id}`
 *
 * When `availableFilters` is provided, it uses the pre-fetched values from the store
 * instead of extracting from the currently loaded documents. This ensures all
 * available filter values for the building are shown regardless of pagination.
 *
 * @param documents - Array of document objects (fallback when availableFilters not provided)
 * @param projects - Optional array of building projects
 * @param availableFilters - Pre-fetched filters from the store
 * @returns Array of TagGroup objects with computed tags and counts
 */
export function useTagGroups({
  documents,
  projects = [],
  availableFilters,
}: UseTagGroupsProps): TagGroup[] {
  const { t, language } = useLanguage();
  const sourceDocuments = availableFilters ? NO_FALLBACK_DOCUMENTS : documents;
  return useMemo(() => {
    let projectTags: Tag[];
    let categoryTags: Tag[];
    let disciplineTags: Tag[];
    let stageTags: Tag[];

    if (availableFilters) {
      // Use pre-fetched filters from the store (all values for building)
      projectTags = buildProjectTagsFromFilters(availableFilters, projects);

      // Use filter groups if available, otherwise fall back to old category hierarchy
      if (availableFilters.filterGroups && availableFilters.filterGroups.length > 0) {
        categoryTags = buildFilterGroupTags(availableFilters.filterGroups, language);
      } else {
        categoryTags = buildCategoryTagsFromFilters(availableFilters.categories, language);
      }

      disciplineTags = buildDisciplineTagsFromFilters(availableFilters.designDisciplines, language);
      stageTags = buildStageTagsFromFilters(availableFilters.designStages, language);
    } else {
      // Fallback: extract from currently loaded documents (may be incomplete due to pagination)
      projectTags = extractProjectTags(sourceDocuments, projects);
      categoryTags = extractCategoryTags(sourceDocuments, language);
      disciplineTags = extractDisciplineTags(sourceDocuments, language);
      stageTags = extractStageTags(sourceDocuments, language);
    }

    const tagsByGroupId: Record<string, Tag[]> = {
      projects: projectTags,
      categories: categoryTags,
      disciplines: disciplineTags,
      stages: stageTags,
    };

    return TAG_GROUP_DEFINITIONS.map((def) => ({
      id: def.id,
      label: t.documents.tagFilter[def.labelKey],
      icon: def.icon,
      color: def.color,
      tags: tagsByGroupId[def.id] || [],
    }));
  }, [sourceDocuments, projects, availableFilters, t, language]);
}
